import { motion } from "framer-motion";
import { MapPin, ExternalLink, Car } from "lucide-react";
import { SCHEDULE, formatSlot, ADDRESS, MAPS_URL, MAPS_EMBED, isOpenNow } from "@/lib/hours";

export default function Horaires() {
  const today = new Date().getDay();
  const open = isOpenNow();

  return (
    <section id="horaires" data-testid="horaires-itineraires-section" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
      >
        <p className="overline-label">Horaires & itinéraire</p>
        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
          Passez nous voir, <span className="font-editorial italic text-terra">on vous attend.</span>
        </h2>
      </motion.div>

      <div className="mt-14 grid gap-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-2xl border border-border/60 bg-surface-2 p-7 sm:p-9"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl font-bold">Nos horaires</h3>
            <span data-testid="horaires-open-badge" className={`badge-tag ${open ? "badge-veg" : "badge-hot"}`}>
              {open ? "Ouvert maintenant" : "Fermé"}
            </span>
          </div>
          <table data-testid="schedule-hours-table" className="mt-6 w-full">
            <tbody>
              {SCHEDULE.map((d) => {
                const isToday = d.dayIndex === today;
                return (
                  <tr key={d.day} className={`border-b border-border/50 last:border-0 ${isToday ? "text-ochre" : ""}`}>
                    <td className="py-3.5 pr-4 font-medium">
                      {d.day}
                      {isToday && <span className="ml-2 font-code text-[9px] uppercase tracking-[0.2em] text-terra">Aujourd'hui</span>}
                    </td>
                    <td className="py-3.5 text-right text-sm text-dim">
                      {d.slots.length === 0
                        ? "Fermé"
                        : d.slots.map(([a, b], i) => (
                            <span key={i} className="block sm:inline">
                              {formatSlot(a)} – {formatSlot(b)}
                              {i < d.slots.length - 1 && <span className="hidden sm:inline">&nbsp; ·&nbsp; </span>}
                            </span>
                          ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-5 text-xs text-dim">Fermé les jours fériés. Dernière prise de commande 15 min avant la fermeture.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="overflow-hidden rounded-2xl border border-border/60 bg-surface-2"
        >
          <div data-testid="map-location-container" className="h-64 w-full sm:h-72">
            <iframe
              title="Plan d'accès — La Bottega del Fuoco"
              src={MAPS_EMBED}
              className="h-full w-full border-0 grayscale-[35%] contrast-[1.05]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="p-7">
            <p data-testid="pizzeria-address-text" className="flex items-start gap-2.5 font-medium">
              <MapPin size={18} className="mt-0.5 shrink-0 text-terra" /> {ADDRESS}
            </p>
            <p className="mt-3 flex items-start gap-2.5 text-sm text-dim">
              <Car size={17} className="mt-0.5 shrink-0 text-olive" /> Parking gratuit à proximité · À 2 minutes de Villefranche-sur-Saône
            </p>
            <a
              data-testid="open-gmaps-button"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost mt-6 !py-2.5 !px-5 text-sm"
            >
              Ouvrir dans Google Maps <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
